import { Component, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService, UtilsService } from 'shared-components';
import { EtiquetadoContextService } from "../../context/etiquetado-context.service";
import { EtiquetadoService } from "../../services/etiquetado.service";
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";
import { SessionContextService } from "projects/packcert-web/src/app/context/session-context.service";
import { Router } from "@angular/router";

@Component({
    selector: 'app-cultivos',
    templateUrl: './cultivos.component.html'
})
export class CultivosComponent implements OnInit, OnDestroy {

    private readonly _DEFAULT_ITEMS_PER_PAGE = 10;
    private _filters;
    public filtersVisible = true;
    public filtersForm: UntypedFormGroup;

    public toCloseSpinner = 0;

    public provincias;
    public municipios;
    public cultivos;
    public denominaciones;
    public campanyas;
    public marcas;

    public cultivosList;
    public paisId;
    public denominacionId;

    public disabledProvincias: Subject<boolean> = new Subject();
    public disabledMunicipios: Subject<boolean> = new Subject();
    public disabledCultivos: Subject<boolean> = new Subject();
    public disabledDenominaciones: Subject<boolean> = new Subject();
    public disabledCampanya: Subject<boolean> = new Subject();
    public disabledMarcas: Subject<boolean> = new Subject();

    public mensajes = new Array<Message>();
    public showError: boolean;
    public showWarning: boolean;

    public dropdownInfo: number;
    public dropdownSection: number;
    public pestanaActiva = new Array<number>();

    public itemsPerPage = this._DEFAULT_ITEMS_PER_PAGE;
    public currentPage = 1;
    public firstItemList = 0;
    public lastItemList = this.itemsPerPage;
    public totalItems: number;
    public readonly visiblePages = 3;

    public idColumn;
    public columnToSort: string;
    public orderBy = 'asc';

    public fileType: string;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private readonly _router: Router,
        private _fb: UntypedFormBuilder,
        private readonly _translate: TranslateService,
        private readonly _spinnerService: SpinnerService,
        private readonly _sessionContext: SessionContextService,
        private readonly _etiquetadoService: EtiquetadoService,
        private readonly _etiquetadoContext: EtiquetadoContextService,
        private readonly _dataProvider: DataProvider,
        private readonly _utilsService: UtilsService
    ) { }

    ngOnInit() {
        this.filtersForm = this._fb.group({
            pais: ['', Validators.required],
            denominacion: [{ value: '', disabled: true }, Validators.required],
            campanya: [{ value: '', disabled: true }, Validators.required],
            marca: [{ value: '', disabled: true }],
            provincia: [{ value: '', disabled: true }],
            municipio: [{ value: '', disabled: true }],
            desFinca: [''],
            nLicencia: [''],
            desProductor: [''],
            fechaInicio: [''],
            fechaFin: ['']
        });

        this._etiquetadoContext.cultivosFilters = null;

        this.checkWarnings(this.filtersForm.get('pais').value);

        this.filtersForm.get('pais').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((pais) => {
            if (pais) {
                this.paisId = pais.id;
                this.getDenominacionesByPais(pais.id);
                this.getProvinciasByPais(pais.id);

            } else {
                this.disabledDenominaciones.next(true);
                this.disabledProvincias.next(true);
                this.denominaciones = [];
                this.provincias = [];
                this.filtersForm.get('denominacion').patchValue('');
                this.filtersForm.get('denominacion').disable();
                this.filtersForm.get('provincia').patchValue('');
                this.filtersForm.get('provincia').disable();
            }
        });

        if (this._dataProvider.paises) {
            if (this._dataProvider.paises.length === 1 && this.filtersForm.get('pais').enabled) {
                this.filtersForm.get('pais').patchValue(this._dataProvider.paises[0]);
                this.filtersForm.get('pais').disable();
            }
        }

        this.filtersForm.get('denominacion').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((denominacion) => {
            if (denominacion) {
                this.denominacionId = denominacion.id;
                this.getCampanyasByDenominacion(denominacion.id, this.paisId);
            } else {
                this.disabledCampanya.next(true);
                this.campanyas = [];
                this.filtersForm.get('campanya').patchValue('');
                this.filtersForm.get('campanya').disable();
            }
        });

        this.filtersForm.get('campanya').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((campanya) => {
            if (campanya) {
                this.getMarcasByCampanya(campanya.id, this.paisId, this.denominacionId);
            } else {
                this.disabledMarcas.next(true);
                this.marcas = [];
                this.filtersForm.get('marca').patchValue('');
                this.filtersForm.get('marca').disable();
            }
        });

        this.filtersForm.get('provincia').valueChanges.pipe(takeUntil(this.destroy$)).subscribe((provincia) => {
            if (provincia) {
                this.getMunicipiosByProvincia(provincia.id, this.paisId);
            } else {
                this.disabledMunicipios.next(true);
                this.municipios = [];
                this.filtersForm.get('municipio').patchValue('');
                this.filtersForm.get('municipio').disable();
            }
        });

        this.filtersForm.get('fechaInicio').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
        this.filtersForm.get('fechaFin').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => this.validDates());
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public get defaultLanguage() {
        return this._translate.getDefaultLang();
    }

    public get paises() {
        return this._dataProvider.paises;
    }

    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }

    public getDenominacionesByPais(paisId) {
        const filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getDenominaciones(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.denominaciones = resp.lista;
                if (this.denominaciones.length === 1) {
                    this.filtersForm.get('denominacion').patchValue(this.denominaciones[0]);
                    this.filtersForm.get('denominacion').disable();
                    this.disabledDenominaciones.next(true);
                } else {
                    this.filtersForm.get('denominacion').enable();
                    this.disabledDenominaciones.next(false);
                }
            }
        });
    }

    public getCampanyasByDenominacion(denominacionId, paisId) {
        const filters = {
            denominacionId: denominacionId,
            paisId: paisId,
            volcadoHistorico: false
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getCampanyas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.campanyas = resp.lista;
                this.campanyas.forEach(campanya => {
                    campanya.des = campanya.anyo.toString();
                });
                this.filtersForm.get('campanya').patchValue(this.campanyas[0]);
                this.filtersForm.get('campanya').enable();
                this.disabledCampanya.next(false);
            }
        });
    }

    public getMarcasByCampanya(campanyaId, paisId, denominacionId) {
        const filters = {
            campanyaId: campanyaId,
            paisId: paisId,
            denominacionId: denominacionId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getMarcas(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.marcas = resp.lista;
                this.marcas.unshift({ id: -1, des: this._translate.instant('global.sinMarca') });
                this.filtersForm.get('marca').enable();
                this.disabledMarcas.next(false);
            }
        });
    }

    public getProvinciasByPais(paisId) {
        const filters = {
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getProvinciasByPaisId(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.provincias = resp.lista;
                this.filtersForm.get('provincia').enable();
                this.disabledProvincias.next(false);
            }
        });
    }

    public getMunicipiosByProvincia(provinciaId, paisId) {
        const filters = {
            provinciaId: provinciaId,
            paisId: paisId
        };
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getMunicipiosByProvinciaId(filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            if (resp.lista.length > 0) {
                this.municipios = resp.lista;
                this.filtersForm.get('municipio').enable();
                this.disabledMunicipios.next(false);
            }
        });
    }

    private checkWarnings(cultivo) {
        if (cultivo && this.filtersForm.valid) {
            this.filtersVisible = false;
            this.showWarning = false;
            this.mensajes = [];
            this.getInfoCultivos();
        } else {
            this.filtersVisible = true;
            this.showWarning = true;
            this.mensajes.push({ class: 'warning', message: 'global.info.filterRequired' });
        }
    }

    public pageChange(page) {
        this.dropdownInfo = -1;
        this.dropdownSection = -1;
        this.currentPage = page;
        this.firstItemList = this.itemsPerPage * (page - 1);
        this.lastItemList = this.itemsPerPage * page;
    }

    public itemsPerPageChange(items) {
        this.dropdownInfo = -1;
        this.dropdownSection = -1;
        this.currentPage = 1;
        this.itemsPerPage = items;
        this.firstItemList = 0;
        this.lastItemList = items;
    }

    public checkValuesForm(): boolean {
        return (
            (this.filtersForm.get('pais').value && this.filtersForm.get('pais').enabled) ||
            (this.filtersForm.get('denominacion').value && this.filtersForm.get('denominacion').enabled) ||
            this.filtersForm.get('campanya').value ||
            this.filtersForm.get('marca').value ||
            this.filtersForm.get('provincia').value ||
            this.filtersForm.get('municipio').value ||
            this.filtersForm.get('desFinca').value ||
            this.filtersForm.get('desProductor').value ||
            this.filtersForm.get('nLicencia').value ||
            this.filtersForm.get('fechaInicio').value ||
            this.filtersForm.get('fechaFin').value) ? true : false;
    }

    public resetForm() {
        this.filtersForm.get('pais').disabled ? this.filtersForm.get('pais').patchValue(this.filtersForm.get('pais').value) : this.filtersForm.controls['pais'].reset();
        this.filtersForm.controls['desFinca'].reset();
        this.filtersForm.controls['desProductor'].reset();
        this.filtersForm.controls['nLicencia'].reset();
        this.filtersForm.controls['fechaInicio'].reset();
        this.filtersForm.controls['fechaFin'].reset();

        this.checkWarnings(this.filtersForm.get('pais').value);
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    public validDates() {
        if (this.filtersForm.get('fechaInicio').value && this.filtersForm.get('fechaFin').value) {
            if (this.checkDates(this.filtersForm.get('fechaInicio').value, this.filtersForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.filtersForm.get('fechaInicio').setErrors(['Invalid date']);
                this.filtersForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.filtersForm.get('fechaInicio').invalid || this.filtersForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private clearDatesValidators() {
        if (this.filtersForm.get('fechaFin').errors && this.filtersForm.get('fechaFin').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaFin').clearValidators();
            this.filtersForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.filtersForm.get('fechaInicio').errors && this.filtersForm.get('fechaInicio').errors.includes('Invalid date')) {
            this.filtersForm.get('fechaInicio').clearValidators();
            this.filtersForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    public filtrar() {
        this.validDates();

        if (this.filtersForm.invalid) {
            if (Object.keys(this.filtersForm.controls).some(key => this.filtersForm.get(key).hasError('required'))) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        } else {
            this.applyFilters();
        }
    }

    public applyFilters() {
        // Reseteamos los iconos de las flechas, la columna y su orden
        this.idColumn = '';
        this.columnToSort = undefined;
        this.orderBy = 'asc';

        this.currentPage = 1;
        this._etiquetadoContext.cultivosFilters = {
            campanyaId: this.filtersForm.get('campanya').value ? this.filtersForm.get('campanya').value.id : '',
            provinciaId: this.filtersForm.get('provincia').value ? this.filtersForm.get('provincia').value.id : '',
            municipioId: this.filtersForm.get('municipio').value ? this.filtersForm.get('municipio').value.id : '',
            marcaId: this.filtersForm.get('marca').value ? this.filtersForm.get('marca').value.id : '',
            desFinca: this.filtersForm.get('desFinca').value ? this.filtersForm.get('desFinca').value : '',
            desProductor: this.filtersForm.get('desProductor').value ? this.filtersForm.get('desProductor').value : '',
            nLicencia: this.filtersForm.get('nLicencia').value ? this.filtersForm.get('nLicencia').value : '',
            fechaInicio: this.filtersForm.get('fechaInicio').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaInicio').value) : '',
            fechaFin: this.filtersForm.get('fechaFin').value ?
                this._utilsService.parseGMTtoYYYYMMDD(this.filtersForm.get('fechaFin').value) : '',
        };

        this.getInfoCultivos();
    }

    public getInfoCultivos() {
        if (this._etiquetadoContext.cultivosFilters) {
            this._filters = this._etiquetadoContext.cultivosFilters;
        } else {
            this.applyFilters();
        }

        // Para ordenar la columna correspondiente
        if (this.columnToSort) {
            this.prepareForSorting();
        }
        this._spinnerService.showSpinner();
        this.toCloseSpinner += 1;
        this._etiquetadoService.getReportCultivos(this._filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then((resp) => {
            this.totalItems = resp.lsTotales.length;
            this.cultivosList = resp;
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public showDetails(i: number) {
        this.dropdownInfo = this.dropdownInfo === i ? -1 : i;
        // Desplazamiento al principio del panel "Más información" para tablet (mismo tamaño máx. que en table.scss)
        if (this.dropdownInfo !== -1 && window.innerWidth <= 1199) {
            // Retardo: en caso de que haya otro detalle abierto
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public openTabs(i) {
        this.dropdownSection = this.dropdownSection === i ? -1 : i;
        this.pestanaActiva[i] = this.pestanaActiva[i] > 0 ? this.pestanaActiva[i] : 1;
        if (this.dropdownSection !== -1 && window.innerWidth <= 1199) {
            setTimeout(() => {
                document.querySelector('table')?.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'start'});
            }, 50);
        }
    }

    public trackByCultivoId(index: number, cultivo: { cultivoId: number }): number {
        return cultivo.cultivoId;
    }

    private checkCloseSpinner() {
        this.toCloseSpinner -= 1;
        if (this.toCloseSpinner === 0) {
            this._spinnerService.hideSpinner();
        }
    }

    private prepareForSorting() {
        this._filters.orderItems = [
            {
                orderProperty: this.columnToSort,
                orderByDescending: this.orderBy === 'desc' ? true : false
            }
        ]
    }

    public exportPdfOrCsv(type) {
        if (this.cultivosList && this.cultivosList.lsTotales.length > 0) {
            if (this._etiquetadoContext.cultivosFilters) {
                this._filters = this._etiquetadoContext.cultivosFilters;
                this._filters.tamPagina = 0;

                switch (type) {
                    case 'Pdf':
                        this.fileType = 'Pdf';
                        break;
                    case 'Csv':
                        this.fileType = 'Csv';
                        break;
                }

                this._filters.tipoArchivo = this.fileType;
            }

            // Si se había ordenado alguna columna de la tabla
            if (this.columnToSort) {
                this.prepareForSorting();
            }

            this._spinnerService.showSpinner();
            this.toCloseSpinner += 1;

            switch (this.fileType) {
                case 'Pdf':
                    this.downloadPdfOrExcel('data:application/pdf;base64,');
                    break;
                case 'Csv':
                    this.downloadCsv('data:text/csv;base64,');
                    break;
            }
        } else {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.exportNoData' });
        }
    }
    
    private downloadCsv(mimeType: string) {
        this._etiquetadoService.getCultivosCsvReport(this._filters).toPromise().finally(() => {
                this.checkCloseSpinner();
            }).then((resp) => {
                const downloadLink = document.createElement('a');
                downloadLink.href = mimeType + resp.base64Archivo;
                downloadLink.download = resp.nombreArchivo;
                downloadLink.click();
            },
            (error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.file' });
            }
        );
    }

    private downloadPdfOrExcel(mimeType: string) {
        this._etiquetadoService.getCultivosPdfOrExcel(this._filters).toPromise().finally(() => {
            this.checkCloseSpinner();
        }).then(
            (resp) => {
            const downloadLink = document.createElement('a');
            downloadLink.href = mimeType + resp.base64Archivo;
            downloadLink.download = resp.nombreArchivo;
            downloadLink.click();
            },
            (error) => {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.file' });
            });
    }

    public toOrderBy(id: any, colToSort: string) {
        this.idColumn = id;
        switch (this.orderBy) {
            case 'asc':
                this.orderBy = 'desc';
                break;
            case 'desc':
                this.orderBy = 'asc';
                break;
        }
        this.columnToSort = colToSort;
        this.getInfoCultivos();
    }

    public getIcon(id: any) {
        let iconClass = 'fa fa-sort';
        if (id === this.idColumn) {
            iconClass = this.orderBy === 'desc' ? 'fa fa-sort-down' : 'fa fa-sort-up';
        }
        return iconClass;
    }

    public openDocHelp() {
      this._spinnerService.showSpinner();
      this.toCloseSpinner += 1;
      this._etiquetadoService.getDocHelp('Labeling_Crops.pdf').toPromise().finally(() => {
          this.checkCloseSpinner();
      }).then((resp) => {
          localStorage.setItem('base64', resp.base64Archivo);
          const url = this._router.serializeUrl(this._router.createUrlTree(['viewPdf']));
          window.open(url, '_blank');
      });
    }
}
